export const performance_dashboard_tutorial_config = {
    pageTitle: 'Performance Management Dashboard',
    page_id: 'performance_dashboard',
    autoStartWelcome: true,
    showFloatingButton: true,
    storagePrefix: 'performance_dashboard',
    
  
    
    // Mock data creators - Dashboard doesn't need mock data since it's a navigation page
    mockDataCreators: {
        // Dashboard page is primarily navigation/overview
        // No mock data generation needed - we just show the navigation elements
    },
    
    // Mock data cleaners
    mockDataCleaners: {
        // No mock data to clean up
    },
    
    // Tutorial step definitions
    steps: [
        // SECTION 1: COMPANY SETUP
        {
            element: [
                '[data-tutorial-element="company_setup"]'
            ],
            title: 'Company Setup Overview',
            description: 'This section guides you through establishing the foundation for your organisation’s performance management framework.',
            additionalInfo: `
                <p><strong>What you'll configure:</strong></p>
                <ul>
                    <li>Company-wide objectives and goals</li>
                    <li>Performance cycles and time periods</li>
                    <li>Rating methods and measurement standards</li>
                    <li>Stage progression for each cycle</li>
                    <li>Access control and permissions</li>
                </ul>
                <p><small>Tip: Complete these steps in order for the best results</small></p>
            `,
            position: 'bottom',
            optional: false
        },
        {
            element: [
                'a[href*="CompanyObjectives"]',
                '.next-step-card-perfm:has(.fa-bullseye)',
                '.page_help_perfm'
            ],
            title: 'Set Company Objectives',
            description: 'Define your organisation’s strategic goals and objectives.',
            purpose: 'Create organizational alignment by setting top-level goals',
            additionalInfo: `
                <p><strong>Purpose:</strong></p>
                <ul>
                    <li>Define strategic organizational goals</li>
                    <li>Enable goal alignment across departments</li>
                    <li>Track company-wide progress</li>
                    <li>Optional feature - use if needed</li>
                </ul>
            `,
            position: 'top',
            optional: true
        },
        {
            element: [
                'a[href*="CycleSetup"]',
                '.next-step-card-perfm .fa-calendar-alt'
            ],
            title: 'Set Company Cycle',
            description: 'Create and configure performance review cycles (e.g., Annual, Semi-Annual, Quarterly) for your organisation.',
            purpose: 'Establish consistent timeframes for performance reviews.',
            additionalInfo: `
                <p><strong>Common Cycle Types:</strong></p>
                <ul>
                    <li><strong>Annual:</strong> Once per year</li>
                    <li><strong>Semi-Annual:</strong> Twice per year</li>
                    <li><strong>Quarterly:</strong> Four times per year</li>
                </ul>
            `,
            position: 'bottom',
            optional: false
        },
        {
            element: [
                'a[href*="RatingSetup"]',
                '.next-step-card-perfm .fa-chart-bar'
            ],
            title: 'Set Rating Methods & Measures',
            description: 'Determine how performance will be rated and measured across your organisation.',
            additionalInfo: `
                <p><strong>Rating Options:</strong></p>
                <ul>
                    <li>Numeric Scale (1-5 or 1-10)</li>
                    <li>Letter Grades (A-F)</li>
                    <li>Descriptive ratings</li>
                </ul>
            `,
            position: 'bottom',
            optional: false
        },
        {
            element: [
                'a[href*="StatusSetup"]',
                '.next-step-card-perfm .fa-users'
            ],
            title: 'Set Stages per Cycle',
            description: 'Configure the workflow stages that staff will progress through during each performance cycle.',
            additionalInfo: `
                <p><strong>Typical Stages:</strong></p>
                <ul>
                    <li>Goal Setting</li>
                    <li>Mid-Cycle Review</li>
                    <li>Management Review</li>
                    <li>Final Rating</li>
                </ul>
            `,
            position: 'bottom',
            optional: false
        },
        {
            element: [
                'a[href*="AccessControl"]',
                '.next-step-card-perfm .fa-shield-alt'
            ],
            title: 'Set Access Control',
            description: 'Define who can view, edit, and manage different aspects of performance data.',
            additionalInfo: `
                <p><strong>Access Levels:</strong></p>
                <ul>
                    <li>View Access</li>
                    <li>Edit Access</li>
                    <li>Admin Access</li>
                    <li>Report Access</li>
                </ul>
            `,
            position: 'bottom',
            optional: false
        },

        // SECTION 2: INDIVIDUAL SETUP
        {
            element: [
                '.individual-section',
                'h2:contains("INDIVIDUAL")'
            ],
            title: 'Individual Setup Overview',
            description: 'Once the company-wide setup is complete, configure individual employee performance plans and objectives',
            additionalInfo: `
                <p><strong>Tasks:</strong></p>
                <ul>
                    <li>Link employees to cycles</li>
                    <li>Set individual objectives</li>
                    <li>Define success criteria</li>
                </ul>
            `,
            position: 'right',
            optional: false
        },
        {
            element: [
                '.a[href*="/employee/view/-1"]',
                '[data-tutorial-element="set_performance_cycle"]'
            ],
            title: 'Set & Link Performance Cycles',
            description: 'Create individual performance plans and connect them to your company’s performance cycles.',
            additionalInfo: `
                <p><strong>Steps:</strong></p>
                <ul>
                    <li> Create a performance plan</li>
                    <li>Link it to the company cycle</li>
                    <li>Assign it to the relevant employee</li>
                </ul>
            `,
            position: 'right',
            optional: false
        },
        {
            element: [
                '[data-tutorial-element="set_obj_measures"]'
            ],
            title: 'Set Objectives & Measures',
            description: 'Define specific, measurable objectives for each employee, along with their success criteria.',
            additionalInfo: `
                <p><strong>Setup Objectives:</strong></p>
                 <ul>
                    <li>Add objectives to the employee’s performance plan</li>
                    <li>Track objectives and measure progress and results</li>
                </ul>
            `,
            position: 'right',
            optional: false
        },

        // SECTION 3: PERFORMANCE PROCESS
        {
            element: [
                '.performance-section',
                'h2:contains("PERFORMANCE PROCESS")'
            ],
            title: 'Performance Process Overview',
            description: 'The ongoing cycle of tracking progress, gathering feedback, and conducting reviews.',
            additionalInfo: `
                <p><strong>Process Stages:</strong></p>
                <ul>
                    <li>Track progress</li>
                    <li>Self-review</li>
                    <li>Peer feedback</li>
                    <li>Manager review</li>
                </ul>
            `,
            position: 'left',
            optional: false
        },
        {
            element: [
                '[data-tutorial-element="performance_rating_report"]'

            ],
            title: 'Execute and Track per Stage',
            description: 'Monitor and manage performance throughout the review cycle.',
            additionalInfo: `
                <p><strong>Features:</strong></p>
                <ul>
                    <li>Progress visualization</li>
                    <li>Stage completion tracking</li>
                    <li>Automated reminders</li>
                </ul>
            `,
            position: 'left',
            optional: false
        },
        {
            element: [
               '[data-tutorial-element="self_review"]'
            ],
            title: 'Self Review & Rating',
            description: 'Employees evaluate their own performance and provide supporting evidence.',
            additionalInfo: `
                <p><strong>Components:</strong></p>
                <ul>
                    <li>Self-rating</li>
                    <li>Achievement descriptions</li>
                    <li>Evidence and examples</li>
                </ul>
            `,
            position: 'left',
            optional: false
        },
        {
            element: [
                '[data-tutorial-element="peer_review"]'
            ],
            title: 'Peer Review & Rating',
            description: 'An optional 360-degree feedback process where colleagues provide input.',
            additionalInfo: `
                <p><strong>Options:</strong></p>
                <ul>
                    <li>Anonymous feedback</li>
                    <li>Named feedback</li>
                    <li>Peer ratings</li>
                </ul>
            `,
            position: 'left',
            optional: true
        },
        {
            element: [
                '[data-tutorial-element="manager_review"]'
            ],
            title: 'Management Review & Rating',
            description: 'Managers conduct formal evaluations and assign final ratings.',
            additionalInfo: `
                <p><strong>Tasks:</strong></p>
                <ul>
                    <li>Rate objectives</li>
                    <li>Provide feedback</li>
                    <li>Determine overall rating</li>
                    <li>Feature key goals</li>
                </ul>
            `,
            position: 'left',
            optional: false
        },
        {
            element: ['.page_help', '.fa-info-circle'],
            title: 'Contextual Help',
            description: 'Click the information icon on any section to access detailed help.',
            requirements: 'Available to all userse',
            additionalInfo: `
                <p><strong>Help covers:</strong></p>
                <ul>
                    <li>Major sections and configuration areas</li>
                    <li>Complex setup features</li>
                    <li>Step-by-step guidance</li>
                </ul>
                <p><small>Tip: Always check contextual help if unsure.</small></p>
            `,
            position: 'left',
            optional: false
        },
    ],

    additionalSelectors: [
        '.next-step-card-perfm',
        '.process-flow',
        '.section',
        '.arrow',
        '[data-bind*="visible:"]'
    ]
};